<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const { t } = useI18n();

const connectionFailed = useMapGetter('phone/getConnectionFailed');
const reason = useMapGetter('phone/getConnectionErrorReason');

const dialogRef = ref(null);

// The generic keys stay the network wording, so an unclassified failure reads
// exactly as it did before.
const title = computed(() => {
  if (reason.value === 'credentials') {
    return t('PHONE_CTRLS.CONNECTION_ERROR.CREDENTIALS_TITLE');
  }
  if (reason.value === 'configuration') {
    return t('PHONE_CTRLS.CONNECTION_ERROR.CONFIGURATION_TITLE');
  }
  return t('PHONE_CTRLS.CONNECTION_ERROR.TITLE');
});

const message = computed(() => {
  if (reason.value === 'credentials') {
    return t('PHONE_CTRLS.CONNECTION_ERROR.CREDENTIALS_MESSAGE');
  }
  if (reason.value === 'configuration') {
    return t('PHONE_CTRLS.CONNECTION_ERROR.CONFIGURATION_MESSAGE');
  }
  return t('PHONE_CTRLS.CONNECTION_ERROR.MESSAGE');
});

// Retrying an unparseable SIP uri produces the same throw every time; only the
// settings can fix it, so don't offer the button.
const showRetry = computed(() => reason.value !== 'configuration');

watch(connectionFailed, failed => {
  if (failed) dialogRef.value?.open();
  else dialogRef.value?.close();
});

// The connect can fail before this component mounts, so don't rely on the
// watcher alone to catch the first failure.
onMounted(() => {
  if (connectionFailed.value) dialogRef.value?.open();
});

const onRetry = () => {
  // Lowers `connectionFailed`, which closes the dialog through the watcher.
  store.dispatch('phone/retryConnect');
};

// Fires on Cancel, Escape, and on our own programmatic closes — the store
// ignores the latter because the flag is already down by then.
const onDismiss = () => {
  store.dispatch('phone/dismissConnectionError');
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    :description="message"
    :show-confirm-button="showRetry"
    :confirm-button-label="t('PHONE_CTRLS.CONNECTION_ERROR.RETRY')"
    :cancel-button-label="t('PHONE_CTRLS.CONNECTION_ERROR.DISMISS')"
    :close-on-click-outside="false"
    width="md"
    @confirm="onRetry"
    @close="onDismiss"
  />
</template>
